// docs/87 — every hotkey in the game, in one place. A hotkey is an id with a label, its default
// keys and the contexts it is live in; the player's rebinds live in `settings.hotkeys` (only the
// ids they changed). Every keyboard handler asks here (`hotkeyFor`, `isHotkey`) instead of
// comparing keys itself, and every on-screen key hint reads its label from here (`keyLabel`).
//
// A key is a chord string: optional `Ctrl+` (Ctrl or Cmd), `Alt+`, `Shift+`, then the physical
// `KeyboardEvent.code` (`KeyW`, `Digit1`, `Space`, `ArrowUp`, `ShiftLeft`). Codes are positional, so
// WASD stays under the left hand on any layout, and Caps Lock changes nothing. Modifiers must match
// exactly: `ArrowUp` pans while `Shift+ArrowUp` walks the tile cursor. A `hold` hotkey (pan, the
// emote wheel) acts while the key is down and takes a bare key, never a chord.
import emotes from '../../../../../content/emotes.json';
import { useSettings } from '../stores/settings';

/** Where a hotkey is live. Two hotkeys may share a key only when they share no context. */
export type HotkeyContext =
  | 'encounter'   // the board: planning, the HUD and its camera
  | 'world'       // the overworld chart and the other screens around it
  | 'story'       // the intro's and a chapter's pages
  | 'cinematic'   // a face-off or a named foe's escape playing over the board
  | 'card'        // a card that waits to be dismissed (Foe Defeated!)
  | 'talk'        // talking with someone in a village
  | 'wheel';      // the emote wheel while it is open (it takes every key)

export type HotkeyGroup = 'Planning' | 'Board' | 'Camera' | 'Hotbar' | 'Story' | 'Emotes' | 'General';

export interface HotkeyDef {
  id: string;
  group: HotkeyGroup;
  label: string;
  defaults: readonly string[];
  contexts: readonly HotkeyContext[];
  /** Acts while held (a bare key; the key's release ends it). */
  hold?: boolean;
}

export const HOTKEY_GROUPS: readonly HotkeyGroup[] = ['Planning', 'Board', 'Camera', 'Hotbar', 'Story', 'Emotes', 'General'];

const ENC: HotkeyContext[] = ['encounter'];
const CAM: HotkeyContext[] = ['encounter', 'world'];
const def = (group: HotkeyGroup, contexts: HotkeyContext[], id: string, label: string, defaults: string[], hold?: boolean): HotkeyDef =>
  ({ id, group, label, defaults, contexts, ...(hold ? { hold } : {}) });

/** The emote wheel's slots: the inner ring on the number row, the outer on the letter rows in reading order, T (the wheel's own key) left out. */
const EMOTE_INNER = 8;
const EMOTE_OUTER_KEYS = 'QWERYUIOPASDFGHJKLZXCVBN'.split('');
const emoteDefaults = (i: number): string[] => {
  if (i < EMOTE_INNER) return [`Digit${i + 1}`, `Numpad${i + 1}`];
  // docs/104 BD: the bard-only emotes past the 32 (ids 32–36) sit on Shift+1… (the wheel lets Shift through)
  if (i >= 32) return [`Shift+Digit${i - 31}`];
  return [`Key${EMOTE_OUTER_KEYS[i - EMOTE_INNER]}`];
};

/** Hotbar slot n (1–9) and Hold (0): docs/33 E3, the number row as drawn on the bar. */
export const HOTBAR_SLOTS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'] as const;
/** A long bar's tenth to eighteenth actions (a wizard's Arcane Ward, a cleric's Dawnlight, a flask) on Shift and the number row. */
export const HOTBAR_MORE = ['s1', 's2', 's3', 's4', 's5', 's6', 's7', 's8', 's9'] as const;
export const hotbarId = (slot: string): string => (slot === '0' ? 'hotbar.hold' : `hotbar.${slot}`);

export const HOTKEYS: readonly HotkeyDef[] = [
  // 11 §7 keyboard grammar (docs/33, docs/40, docs/83)
  def('Planning', ENC, 'board.nextHero', 'Next hero', ['Tab']),
  def('Planning', ENC, 'board.prevHero', 'Previous hero', ['Shift+Tab']),
  def('Planning', ENC, 'board.confirm', 'Confirm', ['Enter']),
  def('Planning', ENC, 'board.back', 'Back / cancel', ['Escape']),
  def('Planning', ENC, 'board.bonus', 'Bonus action slot', ['KeyB']),
  def('Planning', ENC, 'board.hold', 'Hold (leave the slot empty)', ['KeyH']),
  def('Planning', ENC, 'board.undo', 'Undo the last planned slot', ['Ctrl+KeyZ']),
  def('Planning', ENC, 'board.ready', 'Lock / unlock plan', ['Space']),
  def('Planning', ENC, 'board.pause', 'Pause / resume', ['KeyP']),
  def('Planning', ENC, 'board.cursorUp', 'Tile cursor up', ['Shift+ArrowUp']),
  def('Planning', ENC, 'board.cursorDown', 'Tile cursor down', ['Shift+ArrowDown']),
  def('Planning', ENC, 'board.cursorLeft', 'Tile cursor left', ['Shift+ArrowLeft']),
  def('Planning', ENC, 'board.cursorRight', 'Tile cursor right', ['Shift+ArrowRight']),
  def('Planning', ENC, 'board.dragBlock', 'Drag a block (puzzles)', ['ShiftLeft', 'ShiftRight'], true),
  def('Board', ENC, 'board.ping', 'Ping the square under the pointer', ['KeyZ']),
  def('Board', ENC, 'board.warn', 'Warn of danger there', ['Shift+KeyZ']),
  def('Board', ENC, 'board.inventory', 'Gear and bag', ['KeyI']),
  def('Board', ENC, 'board.heroSheet', "Selected hero's stats card", ['KeyK']),
  def('Board', ENC, 'board.heroGear', "Selected hero's gear", ['KeyG']),
  def('Board', ENC, 'board.filter', 'Cycle the view filter', ['KeyF']),
  def('Board', ENC, 'board.minimap', 'Minimap', ['KeyM']),
  // docs/33 E6, docs/42 CM2–CM3, docs/46 T8: the board's and the chart's camera
  def('Camera', CAM, 'camera.panUp', 'Pan up', ['KeyW', 'ArrowUp'], true),
  def('Camera', CAM, 'camera.panLeft', 'Pan left', ['KeyA', 'ArrowLeft'], true),
  def('Camera', CAM, 'camera.panDown', 'Pan down', ['KeyS', 'ArrowDown'], true),
  def('Camera', CAM, 'camera.panRight', 'Pan right', ['KeyD', 'ArrowRight'], true),
  def('Camera', CAM, 'camera.zoomInHold', 'Zoom in', ['KeyE'], true),
  def('Camera', CAM, 'camera.zoomOutHold', 'Zoom out', ['KeyQ'], true),
  def('Camera', CAM, 'camera.zoomIn', 'Zoom in a step', ['Shift+Equal', 'Equal', 'NumpadAdd']),
  def('Camera', CAM, 'camera.zoomOut', 'Zoom out a step', ['Minus', 'Shift+Minus', 'NumpadSubtract']),
  def('Camera', CAM, 'camera.fit', 'Fit the whole board / chart', ['KeyV']),
  def('Camera', ENC, 'camera.hero', 'Centre the selected hero', ['KeyC']),
  ...HOTBAR_SLOTS.map((n) => def('Hotbar', ENC, hotbarId(n), n === '0' ? 'Hold' : `Action ${n}`, [`Digit${n}`, `Numpad${n}`])),
  ...HOTBAR_MORE.map((n, i) => def('Hotbar', ENC, hotbarId(n), `Action ${i + 10}`, [`Shift+Digit${i + 1}`])),
  // docs/21, docs/22, docs/77, docs/86: pages, cinematics and cards
  def('Story', ['story'], 'story.next', 'Next page', ['Space', 'Enter', 'ArrowRight']),
  def('Story', ['story'], 'story.back', 'Previous page', ['ArrowLeft']),
  def('Story', ['cinematic'], 'cine.skip', 'Skip a cinematic', ['Escape']),
  def('Story', ['cinematic'], 'cine.next', 'Step a cinematic on', ['Space']),
  def('Story', ['card'], 'card.dismiss', 'Dismiss a card', ['Enter', 'Space', 'Escape']),
  def('Story', ['talk'], 'talk.leave', 'Stop talking (villages)', ['Escape']),
  // docs/111 TB2: the band's first hero's inventory and stats card, in a village
  def('General', ['world', 'talk'], 'town.inventory', 'Inventory (villages, the World lobby)', ['KeyI']),
  def('General', ['world', 'talk'], 'town.heroSheet', 'Stats card (villages, the World lobby)', ['KeyK']),
  // docs/62: the wheel opens anywhere a hero can be seen
  def('Emotes', ['encounter', 'world', 'story'], 'emote.wheel', 'Emote wheel', ['KeyT'], true),
  def('Emotes', ['wheel'], 'wheel.nextHero', 'Wheel: next hero', ['Tab']),
  def('Emotes', ['wheel'], 'wheel.prevHero', 'Wheel: previous hero', ['Shift+Tab']),
  def('Emotes', ['wheel'], 'wheel.close', 'Wheel: close', ['Escape']),
  ...emotes.emotes.map((e, i) => def('Emotes', ['wheel'], emoteId(e.key), `Emote: ${e.name}`, emoteDefaults(i))),
  def('General', ['world', 'story'], 'menu', 'Menu', ['Escape']),
];

/** docs/33 E6 / docs/42 CM3: the camera's held hotkeys — pan direction, zoom out/in. */
export const CAMERA_PAN: Readonly<Record<string, readonly [number, number]>> = {
  'camera.panUp': [0, -1], 'camera.panLeft': [-1, 0], 'camera.panDown': [0, 1], 'camera.panRight': [1, 0],
};
export const CAMERA_ZOOM: Readonly<Record<string, 1 | -1>> = { 'camera.zoomInHold': 1, 'camera.zoomOutHold': -1 };
export const CAMERA_HOLD_IDS: readonly string[] = [...Object.keys(CAMERA_PAN), ...Object.keys(CAMERA_ZOOM)];

export function emoteId(key: string): string { return `emote.${key}`; }

const BY_ID = new Map(HOTKEYS.map((h) => [h.id, h]));
export const hotkeyDef = (id: string): HotkeyDef | undefined => BY_ID.get(id);

export type Overrides = Readonly<Record<string, readonly string[]>>;
const overridesNow = (): Overrides => useSettings.getState().hotkeys ?? {};

/** The keys bound to a hotkey now (the player's, else the defaults). */
export function bindingsOf(id: string, overrides: Overrides = overridesNow()): readonly string[] {
  return overrides[id] ?? BY_ID.get(id)?.defaults ?? [];
}

const MODIFIER_CODES = new Set(['ShiftLeft', 'ShiftRight', 'ControlLeft', 'ControlRight', 'AltLeft', 'AltRight', 'MetaLeft', 'MetaRight']);
export const isModifierCode = (code: string): boolean => MODIFIER_CODES.has(code);

type KeyLike = Pick<KeyboardEvent, 'code' | 'shiftKey' | 'ctrlKey' | 'metaKey' | 'altKey'>;

/** The chord a key press makes (a modifier key on its own is its bare code). */
export function chordOf(e: KeyLike): string {
  if (isModifierCode(e.code)) return e.code;
  return `${e.ctrlKey || e.metaKey ? 'Ctrl+' : ''}${e.altKey ? 'Alt+' : ''}${e.shiftKey ? 'Shift+' : ''}${e.code}`;
}

/** The bare key of a chord (`Shift+KeyZ` → `KeyZ`). */
export const chordCode = (chord: string): string => chord.slice(chord.lastIndexOf('+') + 1) || chord;

let capturing = false;
/** While the Hotkeys menu waits for a key, no hotkey fires. */
export function setHotkeyCapture(on: boolean): void { capturing = on; }
export const hotkeyCaptureActive = (): boolean => capturing;

/** Is this press the hotkey `id`? */
export function isHotkey(e: KeyLike, id: string, overrides?: Overrides): boolean {
  if (capturing || !e.code) return false;
  return bindingsOf(id, overrides).includes(chordOf(e));
}

/** The first of `ids` this press is, if any. */
export function hotkeyFor<T extends string>(e: KeyLike, ids: readonly T[], overrides: Overrides = overridesNow()): T | undefined {
  if (capturing || !e.code) return undefined;
  const c = chordOf(e);
  return ids.find((id) => bindingsOf(id, overrides).includes(c));
}

/** Is this key release the end of the hold hotkey `id`? (Matched on the bare key, whatever modifiers are down.) */
export function isHotkeyRelease(e: Pick<KeyboardEvent, 'code'>, id: string, overrides?: Overrides): boolean {
  return bindingsOf(id, overrides).some((c) => chordCode(c) === e.code);
}

// ── labels ──

const CODE_LABEL: Record<string, string> = {
  Space: 'Space', Escape: 'Esc', Enter: 'Enter', Tab: 'Tab', Backspace: 'Backspace', Delete: 'Del', Insert: 'Ins', Home: 'Home', End: 'End', PageUp: 'PgUp', PageDown: 'PgDn',
  ArrowUp: '↑', ArrowDown: '↓', ArrowLeft: '←', ArrowRight: '→', Equal: '=', Minus: '−', BracketLeft: '[', BracketRight: ']', Backslash: '\\', Semicolon: ';',
  Quote: "'", Comma: ',', Period: '.', Slash: '/', Backquote: '`', CapsLock: 'Caps', ShiftLeft: 'Shift', ShiftRight: 'Right Shift', ControlLeft: 'Ctrl', ControlRight: 'Right Ctrl',
  AltLeft: 'Alt', AltRight: 'Right Alt', MetaLeft: 'Meta', MetaRight: 'Right Meta', NumpadAdd: 'Num +', NumpadSubtract: 'Num −', NumpadMultiply: 'Num *', NumpadDivide: 'Num /',
  NumpadEnter: 'Num Enter', NumpadDecimal: 'Num .', ContextMenu: 'Menu',
};
/** Chords that read better as the character they type. */
const CHORD_LABEL: Record<string, string> = { 'Shift+Equal': '+', 'Shift+Minus': '_' };

/** How a chord is written on screen (`Shift+KeyZ` → `Shift Z`). */
export function chordLabel(chord: string): string {
  if (CHORD_LABEL[chord]) return CHORD_LABEL[chord];
  const parts = chord.split('+');
  const code = parts.pop() ?? '';
  const key = CODE_LABEL[code] ?? (/^Key[A-Z]$/.test(code) ? code.slice(3) : /^Digit\d$/.test(code) ? code.slice(5) : /^Numpad\d$/.test(code) ? `Num ${code.slice(6)}` : code);
  return [...parts, key].join(' ');
}

/** The label of a hotkey's first key; '' when it is unbound. */
export function keyLabel(id: string, overrides?: Overrides): string {
  const c = bindingsOf(id, overrides)[0];
  return c ? chordLabel(c) : '';
}

/** A hotkey's keys for `aria-keyshortcuts` (`Shift+Z Ctrl+Z`); undefined when unbound. */
export function ariaKeys(id: string, overrides?: Overrides): string | undefined {
  const ks = bindingsOf(id, overrides).map((c) => chordLabel(c).replace(/ (?=.)/g, '+'));
  return ks.length ? ks.join(' ') : undefined;
}

/** `text (key)`, or just `text` when the hotkey is unbound. */
export function withKey(text: string, id: string, overrides?: Overrides): string {
  const k = keyLabel(id, overrides);
  return k ? `${text} (${k})` : text;
}

/** The four pan keys as one hint: `WASD` when each is a single letter or digit, else `↑←↓→`-style joined labels. */
export function panLabel(overrides?: Overrides): string {
  const ls = ['camera.panUp', 'camera.panLeft', 'camera.panDown', 'camera.panRight'].map((id) => keyLabel(id, overrides));
  return ls.every((l) => /^[A-Z0-9]$/.test(l)) ? ls.join('') : ls.filter(Boolean).join(' ');
}

/** The hotbar's span as one hint (`1–9`). */
export function hotbarLabel(overrides?: Overrides): string {
  const a = keyLabel(hotbarId('1'), overrides), b = keyLabel(hotbarId('9'), overrides);
  return a && b ? `${a}–${b}` : a || b;
}

/** A hotkey's label that follows rebinds (React). */
export function useKeyLabel(id: string): string {
  return useSettings((s) => keyLabel(id, s.hotkeys ?? {}));
}
const NONE: Overrides = {};
/** The player's rebinds (React; re-renders when they change). */
export function useHotkeyOverrides(): Overrides {
  return useSettings((s) => s.hotkeys) ?? NONE;
}
/** Anything built from the bindings (React): `useKeys((o) => panLabel(o))`. */
export function useKeys<T>(read: (o: Overrides) => T): T {
  return read(useHotkeyOverrides());
}

// ── rebinding (pure: the Hotkeys menu writes what these return to `settings.hotkeys`) ──

const shareContext = (a: HotkeyDef, b: HotkeyDef) => a.contexts.some((c) => b.contexts.includes(c));

/** The hotkeys that would clash with binding `chord` to `id`: same key, a context in common. */
export function clashesWith(id: string, chord: string, overrides: Overrides): string[] {
  const me = BY_ID.get(id);
  if (!me) return [];
  return HOTKEYS.filter((h) => h.id !== id && shareContext(me, h) && bindingsOf(h.id, overrides).includes(chord)).map((h) => h.id);
}

/** Every clash in a set of bindings (pairs of ids sharing a key and a context); none in the defaults. */
export function allClashes(overrides: Overrides = {}): Array<[string, string, string]> {
  const out: Array<[string, string, string]> = [];
  for (let i = 0; i < HOTKEYS.length; i++) {
    for (let j = i + 1; j < HOTKEYS.length; j++) {
      const a = HOTKEYS[i]!, b = HOTKEYS[j]!;
      if (!shareContext(a, b)) continue;
      const bk = bindingsOf(b.id, overrides);
      for (const c of bindingsOf(a.id, overrides)) if (bk.includes(c)) out.push([a.id, b.id, c]);
    }
  }
  return out;
}

/** Normalise an override map: an id whose keys equal its defaults is dropped. */
function tidy(o: Record<string, readonly string[]>): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const [id, keys] of Object.entries(o)) {
    const d = BY_ID.get(id)?.defaults;
    if (!d) continue;
    if (keys.length === d.length && keys.every((k, i) => k === d[i])) continue;
    out[id] = [...keys];
  }
  return out;
}

/**
 * Bind `chord` to `id` (replacing its key at `at`, else adding it). The key is taken from any
 * hotkey it would clash with, so no two live hotkeys ever share a key; `taken` names them.
 */
export function bindKey(overrides: Overrides, id: string, chord: string, at?: number): { overrides: Record<string, string[]>; taken: string[] } {
  const next: Record<string, readonly string[]> = { ...overrides };
  const taken = clashesWith(id, chord, overrides);
  for (const t of taken) next[t] = bindingsOf(t, overrides).filter((c) => c !== chord);
  const mine = [...bindingsOf(id, overrides)].filter((c, i) => c !== chord || i === at);
  if (at !== undefined && at < mine.length) mine[at] = chord; else if (!mine.includes(chord)) mine.push(chord);
  next[id] = [...new Set(mine)];
  return { overrides: tidy(next), taken };
}

/** Unbind one key of a hotkey. */
export function unbindKey(overrides: Overrides, id: string, chord: string): Record<string, string[]> {
  return tidy({ ...overrides, [id]: bindingsOf(id, overrides).filter((c) => c !== chord) });
}

/** One hotkey back to its defaults (its default keys are taken back from whatever holds them now). */
export function resetKey(overrides: Overrides, id: string): { overrides: Record<string, string[]>; taken: string[] } {
  const rest = { ...overrides };
  delete rest[id];
  const me = BY_ID.get(id);
  let o: Overrides = rest;
  const taken: string[] = [];
  for (const c of me?.defaults ?? []) {
    for (const t of clashesWith(id, c, o)) { o = { ...o, [t]: bindingsOf(t, o).filter((k) => k !== c) }; taken.push(t); }
  }
  return { overrides: tidy(o), taken: [...new Set(taken)] };
}

/** Is this hotkey rebound from its defaults? */
export const isRebound = (id: string, overrides: Overrides): boolean => overrides[id] !== undefined;
